/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { useProfile } from '@/hooks';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalculatorSimple,
  faCalendar,
  faCalendarDays,
  faChartMixed,
  faClock,
  faEllipsis,
  faUserGroup,
} from '@fortawesome/pro-regular-svg-icons';
import { buildLink } from '@/utils';
import { Dispatch, SetStateAction } from 'react';
import { SubMenuType } from './types';
import { Avatar, AvatarFallback, DropdownMenuSeparator } from '@e2n/ui';
import {
  NavItemButton,
  NavItemExternalLink,
  NavItemInternalLink,
} from '@/domain/navigation/features/navigation/components/MobileNavItem/NavItem';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { useRotationsplanAktiviert } from '@/domain/navigation/features/navigation/hooks/useRotationsplanAktiviert/useRotationsplanAktiviert';
import { WithFeatureFlags } from '@/features/featureFlags/WithFeatureFlags';

export function useMenuItems({
  handleClose,
  setActiveSubmenu,
}: {
  handleClose: () => void;
  setActiveSubmenu: Dispatch<SetStateAction<SubMenuType | null>>;
}) {
  const { t } = useTranslation();
  const profile = useProfile();
  const { currentStandort } = useStandortContext();
  const manager = profile.manager;
  const menuItems = [];
  const initials =
    manager.vorname[0].toUpperCase() + manager.nachname[0].toUpperCase();

  const { istAktiviert: istRotationsplanAktiviert } = useRotationsplanAktiviert(
    currentStandort.idStandortgruppe,
  );

  if (manager.hasControllingRecht()) {
    menuItems.push(
      <NavItemButton onClick={() => setActiveSubmenu('controlling')}>
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faChartMixed} />
          {t('entities.controlling')}
        </div>
      </NavItemButton>,
    );
  }

  if (manager.rechte.mitarbeiter.erlaubt) {
    const Fallback = (
      <NavItemExternalLink
        href={buildLink('mitarbeiterListe')}
        key="employee"
        data-testid="menu-item-employee"
        aria-label={t('entities.employee_other')}>
        <FontAwesomeIcon icon={faUserGroup} />
        {currentStandort.land === 'CH'
          ? t('entities.arbeitnehmende')
          : t('entities.employee_other')}
      </NavItemExternalLink>
    );

    menuItems.push(
      <WithFeatureFlags name="rotationsplan" Fallback={Fallback}>
        {istRotationsplanAktiviert &&
        manager.rechte.mitarbeiter.rotationsplan ? (
          <NavItemButton onClick={() => setActiveSubmenu('mitarbeiter')}>
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faUserGroup} />
              {t('entities.employee_one')}
            </div>
          </NavItemButton>
        ) : (
          Fallback
        )}
      </WithFeatureFlags>,
    );
  }

  if (manager.rechte.kalender) {
    menuItems.push(
      <NavItemExternalLink key="kalender" href={buildLink('kalender')}>
        <FontAwesomeIcon icon={faCalendar} />
        {t('entities.calendar')}
      </NavItemExternalLink>,
    );
  }

  if (manager.rechte.arbeitszeit.erlaubt) {
    const arbeitszeitenDesign = manager?.settings.arbeitszeitenDesign;

    if (arbeitszeitenDesign === 'Neu') {
      const currentDate = new Date();
      const arbeitszeitenLink = `/arbeitszeiten/uebersicht/${currentDate.getFullYear()}/${(currentDate.getMonth() + 1).toString().padStart(2, '0')}/${currentDate.getDate().toString().padStart(2, '0')}`;
      menuItems.push(
        <NavItemInternalLink to={arbeitszeitenLink} onClick={handleClose}>
          <FontAwesomeIcon icon={faClock} />
          {t('entities.workingHour_other')}
        </NavItemInternalLink>,
      );
    } else {
      menuItems.push(
        <NavItemExternalLink
          key="arbeitszeit"
          href={buildLink('arbeitszeiten')}>
          <FontAwesomeIcon icon={faClock} />
          {t('entities.workingHour_other')}
        </NavItemExternalLink>,
      );
    }
  }
  //   //TODO: Link austauschen bei Schweizer Mandaten
  if (manager.hasAbrechnungsRecht()) {
    menuItems.push(
      <NavItemExternalLink key="payoff" href={buildLink('abrechnung')}>
        <FontAwesomeIcon icon={faCalculatorSimple} />
        {t('entities.payoff')}
      </NavItemExternalLink>,
    );
  }

  //   // //TODO: Nicht angzeigen bei gesperrtem Mandaten
  if (manager.rechte.dienstplan.erlaubt) {
    menuItems.push(
      <NavItemButton onClick={() => setActiveSubmenu('dienstplan')}>
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faCalendarDays} />
          {t('entities.roster')}
        </div>
      </NavItemButton>,
    );
  }

  if (
    manager.rechte.termine ||
    manager.rechte.mitarbeiter.dokumente ||
    manager.rechte.konflikte
  ) {
    menuItems.push(
      <NavItemButton onClick={() => setActiveSubmenu('mehr')}>
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faEllipsis} />
          {t('general.navigation.moreItems')}
        </div>
      </NavItemButton>,
    );
  }

  menuItems.push(<DropdownMenuSeparator className="mx-2" />);

  menuItems.push(
    <NavItemButton onClick={() => setActiveSubmenu('profil')}>
      <div className="flex items-center gap-2">
        <Avatar className="size-8">
          <AvatarFallback data-testid="navigation-avatar">
            {initials}
          </AvatarFallback>
        </Avatar>
        {manager.vorname + ' ' + manager.nachname}
      </div>
    </NavItemButton>,
  );

  return menuItems;
}
